import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import { CheckCircle2, X, XCircle, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'danger' | 'warning'
type Toast = { id: number; message: string; tone: ToastTone }
type ToastPush = (message: string, tone?: ToastTone) => void

const ToastContext = createContext<ToastPush | null>(null)

// Auto-dismiss windows per tone. Errors get a longer read window than
// success; both still auto-clear so the stack doesn't accumulate. The
// dismiss button is always available for users who want it gone sooner.
const DISMISS_MS: Record<ToastTone, number> = {
  success: 6000,
  warning: 8000,
  danger: 10000,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback<ToastPush>((message, tone = 'success') => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { id, message, tone }])
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, DISMISS_MS[tone])
  }, [])

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-label="Notifications"
        // top-20 clears the fixed header (h-16) on customer + admin shells;
        // on pages without a header the gap is harmless.
        className="pointer-events-none fixed right-4 top-20 z-50 flex flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'success' ? 'status' : 'alert'}
            aria-live={t.tone === 'success' ? 'polite' : 'assertive'}
            className={cn(
              'pointer-events-auto flex min-w-[280px] max-w-[420px] items-start gap-3 rounded-lg border bg-surface px-4 py-3 text-sm',
              'shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_8px_24px_-6px_rgba(11,24,50,0.18),0_2px_6px_-2px_rgba(11,24,50,0.08)]',
              'animate-in slide-in-from-right-5',
              t.tone === 'success' && 'border-l-4 border-l-success border-border',
              t.tone === 'warning' && 'border-l-4 border-l-warning border-border',
              t.tone === 'danger' && 'border-l-4 border-l-danger border-border',
            )}
          >
            {t.tone === 'success' ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            ) : t.tone === 'warning' ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
            )}
            <span className="flex-1 text-text">{t.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setToasts((prev) => prev.filter((p) => p.id !== t.id))}
              className="text-muted hover:text-text transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
